import { useRef, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Config from "../../../Config";
import ModalSimple from "../../blocks/ModalSimple";
import ModalQuestion from "../../blocks/ModalQuestion";
import { v4 as uuidv4 } from 'uuid';
import { useI18n } from "../../../i18n/I18nProvider";
import {
    AuthField,
    AuthFields,
    AuthForm,
    AuthFormHeader,
    AuthFormSubtitle,
    AuthFormTitle,
    AuthLinkButton,
    AuthPrimaryButton,
    AuthPrompt,
} from "./AuthForm.styles";

const LoginForm = () => {
    const { t } = useI18n();
    const formRef = useRef(null);
    const navigate = useNavigate();

    // Заменяем useForm на useState
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isDisabled, setIsDisabled] = useState("");
    const [errors, setErrors] = useState({});

    const [isModalSimpleOpen, setIsModalSimpleOpen] = useState(false);
    const [modalSimpleContent, setModalSimpleContent] = useState("");
    const [modalSimpleTitle, setModalSimpleTitle] = useState("");
    const [modalSimpleType, setModalSimpleType] = useState("error");

    const [isModalQuestionOpen, setIsModalQuestionOpen] = useState(false);
    const [modalQuestionContent, setModalQuestionContent] = useState("");
    const [modalQuestionTitle, setModalQuestionTitle] = useState("");
    const [modalQuestionType, setModalQuestionType] = useState("info");

    const validate = () => {
        const newErrors = {};
        if (!email) newErrors.email = t("Введите email");
        if (!password) newErrors.password = t("Введите пароль");
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const loginSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        const backendAddr = Config.BACKEND_ADDR + "/auth/login";
        try {
            const response = await axios.post(backendAddr, { email, password }, { withCredentials: true });
            if (response.status === 200) {
                let uuid = await localStorage.getItem("uid")
                if (!uuid) {
                    uuid = uuidv4()
                    await localStorage.setItem("uid", uuid)
                }
                try {
                    const loginUUid = await axios.post(
                        Config.BACKEND_ADDR + "/devices/login_device",
                        { uuid },
                        { withCredentials: true },
                    );
                    console.log("Login UUID response:", loginUUid);
                    if (loginUUid.status === 200) {
                        navigate("/service/personal/");
                    }else {
                        setModalSimpleContent(t("Не удалось зарегистрировать устройство."));
                        setModalSimpleTitle(`${t("Ошибка кода")} ${loginUUid.status}`);
                        setModalSimpleType("error");
                        setIsModalSimpleOpen(true);
                    }

                } catch (deviceErr) {
                    console.error("Device login error:", deviceErr);
                    setModalSimpleContent(deviceErr.message || t("Произошла ошибка при регистрации устройства."));
                    setModalSimpleTitle(t("Ошибка"));
                    setModalSimpleType("error");
                    setIsModalSimpleOpen(true);
                }
            } else if (response.status === 401) {
                setModalQuestionContent(t("Проверьте, пожалуйста, логин и пароль. Если вы ещё не регистрировались — сначала создайте аккаунт"));
                setModalQuestionTitle(t("Не удалось войти"));
                setModalQuestionType("error");
                setIsModalQuestionOpen(true);
            }
        } catch (err) {
            if (err.response && err.response.status === 401) {
                setModalQuestionContent(t("Проверьте, пожалуйста, логин и пароль. Если вы ещё не регистрировались — сначала создайте аккаунт"));
                setModalQuestionTitle(t("Не удалось войти"));
                setModalQuestionType("error");
                setIsModalQuestionOpen(true);
            } else {
                setModalSimpleContent(err.message || t("Произошла ошибка при авторизации."));
                setModalSimpleTitle(t("Ошибка"));
                setModalSimpleType("error");
                setIsModalSimpleOpen(true);
            }
        }
    };

        const handleCloseModalSimple = useCallback(() => {
            setIsModalSimpleOpen(false);
        }, []);

    const handleCloseModalQuestion = useCallback((answer) => {
        if (!answer) {
            navigate("/auth/register");
        }
        setIsModalQuestionOpen(false);
    }, []);

    useEffect(() => {
        if(email && password) {
            setIsDisabled(false);
        } else {
            setIsDisabled(true);
        }
    }, [email, password]);

    return (
        <AuthForm ref={formRef} onSubmit={loginSubmit}>
        <ModalSimple
            //type={modalSimpleType}
            type="none"
            h2={modalSimpleTitle}
            p={modalSimpleContent}
            isOpen={isModalSimpleOpen}
            onClose={handleCloseModalSimple}
        />
        <ModalQuestion
            //type={modalQuestionType}
            type="none"
            h2={modalQuestionTitle}
            p={modalQuestionContent}
            isOpen={isModalQuestionOpen}
            onClose={handleCloseModalQuestion}
            yesText={t("Закрыть")}
            noText={t("Зарегистрироваться")}
            isButtonRevers={false}
        ></ModalQuestion>
            <AuthFormHeader>
                <AuthFormTitle>{t("Авторизация")}</AuthFormTitle>
                <AuthFormSubtitle>
                    {t("Продолжайте с того места, где остановились")}
                </AuthFormSubtitle>
            </AuthFormHeader>
            <AuthFields>
                <AuthField>
                    Email
                    <input
                        placeholder="example@mail.ru"
                        type="email"
                        value={email}
                        autoComplete="email"
                        aria-invalid={Boolean(errors.email)}
                        onChange={(event) => setEmail(event.target.value)}
                    />
                </AuthField>
                <AuthField>
                    {t("Пароль")}
                    <input
                        placeholder={t("Введите пароль")}
                        type="password"
                        value={password}
                        autoComplete="current-password"
                        aria-invalid={Boolean(errors.password)}
                        onChange={(event) => setPassword(event.target.value)}
                    />
                </AuthField>
                <AuthLinkButton type="button" onClick={() => navigate("/auth/recovery")}>
                    {t("Забыли пароль?")}
                </AuthLinkButton>
            </AuthFields>
            <AuthPrimaryButton disabled={isDisabled} type="submit">
                {t("Войти")}
            </AuthPrimaryButton>
            <AuthPrompt>
                <span>{t("Нет аккаунта?")}</span>
                <button type="button" onClick={() => navigate("/auth/register")}>
                    {t("Зарегистрируйтесь")}
                </button>
            </AuthPrompt>
        </AuthForm>
    );
}
export default LoginForm;